import React from "react";
import {
  CustomFieldSchemaUnauthenticatedFragment,
  DisplayAs,
} from "__generated__/graphqlUnauthenticated";
import { FormikPreviewAutoCompleteField } from "../fields/FormikPreviewAutoCompleteField";
import { FormikPreviewFormTextField } from "./fields/FormikPreviewFormTextField";
import { FormikPreviewFormSelect } from "./fields/FormikPreviewFormSelect";
import { FormikPreviewFormRichText } from "./fields/FormikPreviewFormRichText";
import { FormikPreviewFormNumber } from "./fields/FormikPreviewFormNumber";
import { FormkPreviewFormDate } from "./fields/FormkPreviewFormDate";
import { FormikPreviewFormMultiAutoComplete } from "./fields/FormikPreviewFormMultiAutoComplete";
import { FormikPreviewFormMultiSelect } from "./fields/FormikPreviewFormMultiSelect";
import { SxProps } from "@mui/material";
import { FormikPreviewCurrencyField } from "./fields/FormikPreviewCurrencyField";
import { FormikPreviewCountryField } from "../fields/FormikPreviewCountryField";
import { FormikCheckboxField } from "common/fields/FormikCheckboxField";
import { FormikDateTimePicker } from "common/fields/FormikDateTimePicker";
import { useTranslation } from "react-i18next";

interface IPreviewFormInputFieldProps {
  type: DisplayAs | null;
  label: string;
  placeholder: string;
  labelledById: string;
  name: string;
  schema?: CustomFieldSchemaUnauthenticatedFragment | null;
  sx?: SxProps;
  required?: boolean;
}
export const PreviewFormInputField: React.FC<IPreviewFormInputFieldProps> = ({
  type,
  name,
  label,
  labelledById,
  placeholder,
  schema,
  sx,
  required,
}) => {
  const { t: translate } = useTranslation("lists");
  switch (type) {
    case DisplayAs.Text:
      return (
        <FormikPreviewFormTextField
          name={name}
          placeholder={placeholder}
          label={label}
          labelledById={labelledById}
          sx={{ ...sx }}
        />
      );
    case DisplayAs.OptionalValue:
      return (
        <FormikPreviewFormSelect
          name={name}
          labelledById={labelledById}
          schema={schema}
        />
      );
    case DisplayAs.RadioButton:
      return (
        <FormikPreviewFormSelect
          name={name}
          labelledById={labelledById}
          schema={schema}
          listView
        />
      );
    case DisplayAs.MultiOptionalValue:
      return (
        <FormikPreviewFormMultiSelect
          name={name}
          labelledById={labelledById}
          schema={schema}
        />
      );
    case DisplayAs.Checkbox:
      return (
        <FormikPreviewFormMultiSelect
          name={name}
          labelledById={labelledById}
          schema={schema}
          listView
        />
      );
    case DisplayAs.RichText:
      return <FormikPreviewFormRichText name={name} />;
    case DisplayAs.Number:
      return (
        <FormikPreviewFormNumber
          name={name}
          placeholder={placeholder}
          label={label}
          labelledById={labelledById}
        />
      );
    case DisplayAs.Datetime:
      return <FormkPreviewFormDate name={name} labelledById={labelledById} />;
    case DisplayAs.MultiCountry:
      return (
        <FormikPreviewFormMultiAutoComplete
          name={name}
          labelledById={labelledById}
        />
      );
    case DisplayAs.Currency:
      return (
        <FormikPreviewCurrencyField
          name={name}
          labelledById={labelledById}
          schema={schema}
          placeholder={placeholder}
        />
      );
    case DisplayAs.Tags:
      return (
        <FormikPreviewAutoCompleteField
          name={name}
          labelledById={labelledById}
          placeholder={placeholder}
        />
      );
    case DisplayAs.Country:
      return (
        <FormikPreviewCountryField name={name} labelledById={labelledById} />
      );
    case DisplayAs.ConsentStatus:
      return (
        <FormikCheckboxField
          name={name}
          label={label}
          labelVariant="subtitle2"
          isRequired={required}
          labelId={labelledById}
        />
      );
    case DisplayAs.FullDateTime:
      return (
        <FormikDateTimePicker
          name={name}
          label={label}
          labelledById={labelledById}
        />
      );
    default:
      return (
        <div>
          {type} {translate("forms.preview.display_not_supported")}
        </div>
      );
  }
};
